import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms'
import { TableModule } from 'primeng/table'
import { InputNumberModule } from 'primeng/inputnumber'
import { ButtonModule } from 'primeng/button'
import { TooltipModule } from 'primeng/tooltip'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Store } from '@ngxs/store'
import { UserState } from '../../../store'
import { CareType } from '../../../models/care-type.model'
import { AssistanceCareLogRow, CareLogEditorAssistant } from '../../../models/assistance-care-log.model'
import { CareTypeService } from '../../../services/domain/codelists/care-types.service'
import { AssistanceCareLogService } from '../../../services/domain/assistance-care-log.service'
import { ConfirmationService } from 'primeng/api'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import {
  CareTypeSelectModalComponent,
  type CareTypeSelectModalData,
} from './care-type-select-modal/care-type-select-modal.component'

function syntheticIdForPending(assistantId: number, careTypeId: number): number {
  return -(assistantId * 10000 + careTypeId)
}

function sortCareLogRows(rows: ReadonlyArray<AssistanceCareLogRow>): AssistanceCareLogRow[] {
  return [...rows].sort((a, b) => {
    if (a.careTypeId !== b.careTypeId) {
      return a.careTypeId - b.careTypeId
    }
    const idA = a.id ?? 0
    const idB = b.id ?? 0
    return idA - idB
  })
}

const ERROR_ZERO = 'ASSISTANCE_CARE_LOG_EDITOR.ERROR_ZERO'
const ERROR_EXCEEDS = 'ASSISTANCE_CARE_LOG_EDITOR.ERROR_EXCEEDS'
const REMOVE_CONFIRM_HEADER_KEY = 'ASSISTANCE_CARE_LOG_EDITOR.REMOVE_ENTRY_WITH_DURATION_CONFIRM_HEADER'
const REMOVE_CONFIRM_KEY = 'ASSISTANCE_CARE_LOG_EDITOR.REMOVE_ENTRY_WITH_DURATION_CONFIRM'
const CONFIRM_YES_KEY = 'ASSISTANCE_CARE_LOG_EDITOR.CONFIRM_YES'
const CONFIRM_NO_KEY = 'ASSISTANCE_CARE_LOG_EDITOR.CONFIRM_NO'

@Component({
  selector: 'app-assistance-care-log-editor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    InputNumberModule,
    ButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    DynamicDialogModule,
    TranslocoModule,
  ],
  templateUrl: './assistance-care-log-editor.component.html',
  styleUrls: ['./assistance-care-log-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssistanceCareLogEditorComponent),
      multi: true,
    },
    DialogService,
    ConfirmationService,
  ],
})
export class AssistanceCareLogEditorComponent implements ControlValueAccessor {
  private readonly careTypeService = inject(CareTypeService)
  private readonly careLogService = inject(AssistanceCareLogService)
  private readonly store = inject(Store)
  private readonly destroyRef = inject(DestroyRef)
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly dialogService = inject(DialogService)
  private readonly confirmationService = inject(ConfirmationService)
  private readonly transloco = inject(TranslocoService)

  readonly assignments = input<ReadonlyArray<CareLogEditorAssistant>>([])
  readonly assistanceId = input<number | null>(null)
  readonly readonly = input<boolean>(false)
  readonly role = input<'assistant' | 'coordinator'>('coordinator')
  readonly currentAssistantId = input<number | null>(null)
  readonly maxTotalMinutes = input<number | null>(null)
  readonly clientId = input<number | null>(null)

  private _onChange: (value: number) => void = () => {}
  private _onTouched: () => void = () => {}
  private _disabled = false

  readonly effectiveAssignments = computed(() => {
    const all = this.assignments()
    if (this.role() === 'assistant' && this.currentAssistantId() != null) {
      return all.filter((a) => a.assistantId === this.currentAssistantId())
    }
    return all
  })

  readonly totalMinutesForAssistant = computed(() => {
    const aid = this.currentAssistantId()
    if (aid == null) return 0
    const saved = this.careLogsByAssistant().get(aid) ?? []
    const pending = this.pendingLogs().get(aid) ?? []
    return [...saved, ...pending].reduce((s, l) => s + l.careDurationMinutes, 0)
  })
  readonly canAddMoreTasks = computed(() => {
    const max = this.maxTotalMinutes()
    if (max == null || this.role() !== 'assistant') return true
    return this.totalMinutesForAssistant() < max
  })
  readonly remainingMinutes = computed(() => {
    const max = this.maxTotalMinutes()
    if (max == null) return null
    return Math.max(0, max - this.totalMinutesForAssistant())
  })

  readonly careTypeOptions = signal<CareType[]>([])
  readonly careLogsByAssistant = signal<Map<number, AssistanceCareLogRow[]>>(new Map())
  readonly pendingLogs = signal<Map<number, AssistanceCareLogRow[]>>(new Map())
  readonly rowErrors = signal<Map<number, string>>(new Map())

  private readonly originalCellValues = new Map<number, number>()

  /** Stable array for p-table, avoids new reference every CD and table flickering. */
  readonly logsForAssistantMap = computed(() => {
    const saved = this.careLogsByAssistant()
    const pending = this.pendingLogs()
    const assistantIds = new Set<number>([...saved.keys(), ...pending.keys()])
    const map = new Map<number, AssistanceCareLogRow[]>()
    for (const aid of assistantIds) {
      const s = saved.get(aid) ?? []
      const p = pending.get(aid) ?? []
      map.set(aid, sortCareLogRows([...s, ...p]))
    }
    return map
  })

  constructor() {
    effect((onCleanup) => {
      const assignments = this.effectiveAssignments()
      const assignmentIds = assignments.map((a) => a.assignmentId)
      if (assignmentIds.length === 0) {
        this.careLogsByAssistant.set(new Map())
        return
      }
      const sub = this.careLogService.find({ assignmentIds }).subscribe((logs) => {
        const currentOptions = this.careTypeOptions()
        const map = new Map<number, AssistanceCareLogRow[]>()
        for (const log of logs) {
          const assignment = assignments.find((a) => a.assignmentId === log.assignmentId)
          if (!assignment) continue
          const careType = currentOptions.find((ct) => ct.id === log.careTypeId)
          const row: AssistanceCareLogRow = {
            ...log,
            assistantId: assignment.assistantId,
            careTypeName: careType?.name ?? '',
          }
          const list = map.get(assignment.assistantId) ?? []
          list.push(row)
          map.set(assignment.assistantId, list)
        }
        const sortedMap = new Map<number, AssistanceCareLogRow[]>()
        map.forEach((rows, aid) => sortedMap.set(aid, sortCareLogRows(rows)))
        this.careLogsByAssistant.set(sortedMap)
      })
      onCleanup(() => sub.unsubscribe())
    })

    effect(() => {
      const options = this.careTypeOptions()
      if (options.length === 0) return
      this.careLogsByAssistant.update((map) => {
        const next = new Map<number, AssistanceCareLogRow[]>()
        map.forEach((rows, aid) => {
          next.set(
            aid,
            sortCareLogRows(
              rows.map((r) => ({
                ...r,
                careTypeName: options.find((ct) => ct.id === r.careTypeId)?.name ?? r.careTypeName,
              })),
            ),
          )
        })
        return next
      })
    })

    effect(() => {
      const total = this.totalMinutesForAssistant()
      this._onChange(total)
      this._onTouched()
    })

    const activeAllocation = this.store.selectSnapshot(UserState.activeAllocationCurrentUser)
    this.careTypeService
      .getCareTypesForActiveSocialServiceId(activeAllocation?.socialServiceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.careTypeOptions.set([...options]))
  }

  confirmRemoveIfHasDuration(_careTypeId: number): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        header: this.transloco.translate(REMOVE_CONFIRM_HEADER_KEY),
        message: this.transloco.translate(REMOVE_CONFIRM_KEY),
        acceptLabel: this.transloco.translate(CONFIRM_YES_KEY),
        rejectLabel: this.transloco.translate(CONFIRM_NO_KEY),
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: () => resolve(true),
        reject: () => resolve(false),
      })
    })
  }

  openAdd(assistantId: number): void {
    const clientId = this.clientId()
    const assignment = this.effectiveAssignments().find((a) => a.assistantId === assistantId)
    if (clientId == null || !assignment) return

    const options = this.careTypeOptions()
    const saved = this.careLogsByAssistant().get(assistantId) ?? []
    const pending = this.pendingLogs().get(assistantId) ?? []
    const idsInTable = new Set([...saved, ...pending].map((l) => l.careTypeId))
    const careTypeIdsWithDuration = new Set(
      [...saved, ...pending].filter((r) => r.careDurationMinutes > 0).map((r) => r.careTypeId),
    )
    const selectedCareTypes = options.filter((ct) => idsInTable.has(ct.id))

    const data: CareTypeSelectModalData = {
      options,
      selectedCareTypes,
      careTypeIdsWithDuration,
      confirmRemoveIfHasDuration: (careTypeId: number) => this.confirmRemoveIfHasDuration(careTypeId),
      onSelectionChange: (sel) => this.applyModalSelection(sel, assistantId, assignment.assignmentId, clientId),
    }

    const ref = this.dialogService.open(CareTypeSelectModalComponent, {
      header: this.transloco.translate('ASSISTANCE_CARE_LOG_EDITOR.SELECT_FROM_OFFER'),
      closable: true,
      draggable: false,
      modal: true,
      baseZIndex: 100000, // Solving issue with focus input in modal
      autoZIndex: true, // Solving issue with focus input in modal
      appendTo: document.querySelector('ion-modal') ?? 'body', // Solving issue with focus input in modal
      data,
    })

    ref.onClose.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result: CareType[] | null) => {
      if (result == null) return
      this.applyModalSelection(result, assistantId, assignment.assignmentId, clientId)
    })
  }

  private applyModalSelection(
    newSelection: CareType[],
    assistantId: number,
    assignmentId: number,
    clientId: number,
  ): void {
    const saved = this.careLogsByAssistant().get(assistantId) ?? []
    const pending = this.pendingLogs().get(assistantId) ?? []
    const newSelectionIds = new Set(newSelection.map((ct) => ct.id))
    const toAdd = newSelection.filter(
      (ct) => !saved.some((r) => r.careTypeId === ct.id) && !pending.some((r) => r.careTypeId === ct.id),
    )
    const toRemove = [...saved, ...pending].filter((row) => !newSelectionIds.has(row.careTypeId))

    for (const log of toRemove) {
      this.removeLog(log, { skipConfirmation: true })
    }

    if (toAdd.length === 0) return
    const assignment = this.effectiveAssignments().find((a) => a.assistantId === assistantId)
    if (!assignment) return
    const currentPending = this.pendingLogs().get(assistantId) ?? []
    const newPendingRows: AssistanceCareLogRow[] = toAdd.map((careType) => {
      const syntheticId = syntheticIdForPending(assistantId, careType.id)
      return {
        id: syntheticId,
        assignmentId,
        clientId,
        careTypeId: careType.id,
        careDurationMinutes: 0,
        assistantId,
        careTypeName: careType.name,
      }
    })
    this.pendingLogs.update((m) => {
      const next = new Map(m)
      next.set(assistantId, [...currentPending, ...newPendingRows])
      return next
    })
    this.rowErrors.update((m) => {
      const next = new Map(m)
      for (const row of newPendingRows) {
        next.set(row.id!, ERROR_ZERO)
      }
      return next
    })
    this.cdr.markForCheck()
  }

  onCellEditInit(event: { data: number }): void {
    const log = this.findLogById(event.data)
    if (log?.id != null) {
      this.originalCellValues.set(log.id, this.normalizeDurationMinutes(log.careDurationMinutes))
    }
  }

  onCellEditComplete(event: { data: number }): void {
    const logId = event.data
    const original = this.originalCellValues.get(logId)
    this.originalCellValues.delete(logId)
    const log = this.findLogById(logId)
    if (log) {
      if (original !== undefined) {
        const current = this.normalizeDurationMinutes(log.careDurationMinutes)
        const originalNorm = this.normalizeDurationMinutes(original)
        if (current === originalNorm) {
          ;(document.activeElement as HTMLElement | null)?.blur()
          this.cdr.markForCheck()
          return
        }
      }
      this.commitDuration(log)
    }
    ;(document.activeElement as HTMLElement | null)?.blur()
    this.cdr.markForCheck()
  }

  onCellEditCancel(event: { data: number }): void {
    const logId = event.data
    const original = this.originalCellValues.get(logId)
    this.originalCellValues.delete(logId)
    const log = this.findLogById(logId)
    if (original != null && log != null) {
      this.updateRowDuration(log.id!, log.assistantId, original)
      this.rowErrors.update((m) => {
        const next = new Map(m)
        next.delete(log.id!)
        return next
      })
    }
    ;(document.activeElement as HTMLElement | null)?.blur()
    this.cdr.markForCheck()
  }

  private normalizeDurationMinutes(value: number | null | undefined): number {
    if (value == null || Number.isNaN(value)) {
      return 0
    }
    return Math.max(0, Math.floor(value))
  }

  onDurationInputChange(log: AssistanceCareLogRow, value: number | null): void {
    const num = this.normalizeDurationMinutes(value)
    this.updateRowDuration(log.id!, log.assistantId, num)
    const updatedLog: AssistanceCareLogRow = { ...log, careDurationMinutes: num }
    const error = this.validateRow(updatedLog)
    this.rowErrors.update((m) => {
      const next = new Map(m)
      if (error) next.set(log.id!, error)
      else next.delete(log.id!)
      return next
    })
  }

  private updateRowDuration(logId: number, assistantId: number, nextValue: number): void {
    const updateList = (rows: AssistanceCareLogRow[]): AssistanceCareLogRow[] | null => {
      const idx = rows.findIndex((r) => r.id === logId)
      if (idx < 0) return null
      return rows.map((r, i) => (i === idx ? { ...r, careDurationMinutes: nextValue } : r))
    }
    if (logId < 0) {
      this.pendingLogs.update((m) => {
        const list = m.get(assistantId) ?? []
        const nextList = updateList(list)
        if (nextList == null) return m
        const next = new Map(m)
        next.set(assistantId, nextList)
        return next
      })
    } else {
      this.careLogsByAssistant.update((m) => {
        const list = m.get(assistantId) ?? []
        const nextList = updateList(list)
        if (nextList == null) return m
        const next = new Map(m)
        next.set(assistantId, nextList)
        return next
      })
    }
  }

  private validateRow(log: AssistanceCareLogRow): string | null {
    if (log.careDurationMinutes <= 0) return ERROR_ZERO
    if (this.role() === 'assistant' && this.maxTotalMinutes() != null) {
      const all = this.logsForAssistantMap().get(log.assistantId) ?? []
      const otherTotal = all.reduce((s, l) => s + (l.id === log.id ? 0 : l.careDurationMinutes), 0)
      const allowed = this.maxTotalMinutes()! - otherTotal
      if (log.careDurationMinutes > allowed) return ERROR_EXCEEDS
    }
    return null
  }

  commitDuration(log: AssistanceCareLogRow): void {
    if (log.id === null || log.id === undefined) return
    const currentLog = this.findLogById(log.id)
    if (!currentLog) return

    const error = this.validateRow(currentLog)
    if (error) {
      this.rowErrors.update((m) => new Map(m).set(log.id!, error))
      return
    }

    this.rowErrors.update((m) => {
      const next = new Map(m)
      next.delete(log.id!)
      return next
    })

    const isPending = currentLog.id! < 0
    if (isPending) {
      this.careLogService
        .create({
          assignmentId: currentLog.assignmentId,
          clientId: currentLog.clientId,
          careTypeId: currentLog.careTypeId,
          careDurationMinutes: currentLog.careDurationMinutes,
        })
        .then(() => {
          if (this.destroyRef.destroyed) return
          this.pendingLogs.update((m) => {
            const next = new Map(m)
            const list = (next.get(currentLog.assistantId) ?? []).filter((l) => l.id !== currentLog.id)
            if (list.length === 0) next.delete(currentLog.assistantId)
            else next.set(currentLog.assistantId, list)
            return next
          })
          this.cleanupRowErrorsForRemovedLogs()
          this.refreshLogs()
        })
        .catch(() => {
          if (this.destroyRef.destroyed) return
          this.refreshLogs()
        })
    } else {
      this.careLogService
        .patch(currentLog.id!, {
          careDurationMinutes: currentLog.careDurationMinutes,
        })
        .then(() => {
          if (this.destroyRef.destroyed) return
          this.refreshLogs()
        })
        .catch(() => {
          if (this.destroyRef.destroyed) return
          this.refreshLogs()
        })
    }
  }

  private findLogById(logId: number): AssistanceCareLogRow | null {
    for (const rows of this.careLogsByAssistant().values()) {
      const found = rows.find((r) => r.id === logId)
      if (found) return found
    }
    for (const rows of this.pendingLogs().values()) {
      const found = rows.find((r) => r.id === logId)
      if (found) return found
    }
    return null
  }

  removeLog(log: AssistanceCareLogRow, opts?: { skipConfirmation?: boolean }): void {
    if (log.id == null) return
    const doRemove = (): void => {
      if (log.id! < 0) {
        this.pendingLogs.update((m) => {
          const next = new Map(m)
          const list = (next.get(log.assistantId) ?? []).filter((l) => l.id !== log.id)
          if (list.length === 0) next.delete(log.assistantId)
          else next.set(log.assistantId, list)
          return next
        })
        this.cleanupRowErrorsForRemovedLogs()
        return
      }
      this.careLogService
        .remove(log.id)
        .then(() => {
          if (this.destroyRef.destroyed) return
          this.cleanupRowErrorsForRemovedLogs()
          this.refreshLogs()
        })
        .catch(() => {
          if (this.destroyRef.destroyed) return
          this.refreshLogs()
        })
    }
    if (log.id >= 0 && log.careDurationMinutes > 0 && !opts?.skipConfirmation) {
      this.confirmationService.confirm({
        header: this.transloco.translate(REMOVE_CONFIRM_HEADER_KEY),
        message: this.transloco.translate(REMOVE_CONFIRM_KEY),
        acceptLabel: this.transloco.translate(CONFIRM_YES_KEY),
        rejectLabel: this.transloco.translate(CONFIRM_NO_KEY),
        icon: 'pi pi-exclamation-triangle',
        acceptButtonStyleClass: 'p-button-danger',
        rejectButtonStyleClass: 'p-button-secondary',
        accept: doRemove,
      })
    } else {
      doRemove()
    }
  }

  private cleanupRowErrorsForRemovedLogs(): void {
    const allLogIds = new Set<number>()
    for (const rows of this.careLogsByAssistant().values()) {
      for (const r of rows) if (r.id != null) allLogIds.add(r.id)
    }
    for (const rows of this.pendingLogs().values()) {
      for (const r of rows) if (r.id != null) allLogIds.add(r.id)
    }
    this.rowErrors.update((m) => {
      const next = new Map(m)
      for (const id of next.keys()) if (!allLogIds.has(id)) next.delete(id)
      return next
    })
  }

  trackByLogId(index: number, log: AssistanceCareLogRow): number {
    return log.id ?? index
  }

  trackByAssignmentId(_index: number, a: CareLogEditorAssistant): number {
    return a.assignmentId
  }

  writeValue(value: number): void {}

  registerOnChange(fn: (value: number) => void): void {
    this._onChange = fn
    fn(this.totalMinutesForAssistant())
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = isDisabled
  }

  private refreshLogs(): void {
    const assignments = this.effectiveAssignments()
    const assignmentIds = assignments.map((a) => a.assignmentId)
    const options = this.careTypeOptions()
    if (assignmentIds.length === 0) {
      this.careLogsByAssistant.set(new Map())
      return
    }
    this.careLogService
      .find({ assignmentIds })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((logs) => {
        const map = new Map<number, AssistanceCareLogRow[]>()
        for (const log of logs) {
          const assignment = assignments.find((a) => a.assignmentId === log.assignmentId)
          if (!assignment) continue
          const careType = options.find((ct) => ct.id === log.careTypeId)
          const row: AssistanceCareLogRow = {
            ...log,
            assistantId: assignment.assistantId,
            careTypeName: careType?.name ?? '',
          }
          const list = map.get(assignment.assistantId) ?? []
          list.push(row)
          map.set(assignment.assistantId, list)
        }
        const sortedMap = new Map<number, AssistanceCareLogRow[]>()
        map.forEach((rows, aid) => sortedMap.set(aid, sortCareLogRows(rows)))
        this.careLogsByAssistant.set(sortedMap)
        this.cleanupRowErrorsForRemovedLogs()
      })
  }
}
